"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _axios = _interopRequireDefault(require("axios"));
var _bluebird = _interopRequireDefault(require("bluebird"));
var _browserOrNode = require("browser-or-node");
var _lodash = _interopRequireDefault(require("lodash"));
var _uuid = _interopRequireDefault(require("uuid"));
var _basic_provider = _interopRequireDefault(require("./_basic_provider"));
class FilesApi extends _basic_provider.default {
  /**
   * @constructor
   */
  constructor(_ref) {
    let {
      userTokenFactory,
      fingerPrintTokenFactory,
      filesApiUrl,
      accountId,
      sdkApiUrl,
      providersAccountId
    } = _ref;
    super({
      userTokenFactory,
      fingerPrintTokenFactory,
      sdkApiUrl,
      providersAccountId
    });
    this.filesApiUrl = filesApiUrl;
    this.accountId = accountId;
  }
  static _buildQueryParams(mapping) {
    return _lodash.default.chain(mapping).map((value, key) => `${key}=${!_lodash.default.isNil(value) ? encodeURIComponent(value) : ''}`).join('&').value();
  }
  async serviceMetadata() {
    const headers = await this.getHeaders();
    return _bluebird.default.try(() => _axios.default.get(`${this.filesApiUrl}/api/meta`, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async headObject(key, isPublic) {
    const headers = await this.getHeaders();
    return _bluebird.default.try(() => _axios.default.get(`${this.filesApiUrl}/api/file?${FilesApi._buildQueryParams({
      key,
      isPublic,
      ...(this.accountId ? {
        accountId: this.accountId
      } : {})
    })}`, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async headFolder(key) {
    const headers = await this.getHeaders();
    return _bluebird.default.try(() => _axios.default.get(`${this.filesApiUrl}/api/folder?${FilesApi._buildQueryParams({
      key,
      ...(this.accountId ? {
        accountId: this.accountId
      } : {})
    })}`, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async getDownloadUrl(key, isPublic, expires) {
    let invalidateCache = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    const headers = await this.getHeaders();
    const url = await _bluebird.default.try(() => _axios.default.get(`${this.filesApiUrl}/api/url?${FilesApi._buildQueryParams({
      key,
      isPublic,
      expires,
      ...(this.accountId ? {
        accountId: this.accountId
      } : {})
    })}`, headers).then(response => _bluebird.default.resolve(response.data)));
    if (isPublic && invalidateCache) return `${url}?invalidateCache=${_uuid.default.v4()}`;
    return url;
  }
  async getSystemDownloadUrl(postfix, name, options) {
    return _bluebird.default.try(() => this._callApi({
      method: 'POST',
      path: 'downloadurl',
      payload: {
        postfix,
        name,
        ...(!_lodash.default.isEmpty(options) && options)
      }
    }));
  }
  async getUploadUrl(_ref2, isPublic, expires) {
    let {
      key,
      contentType,
      maxFileSize,
      cacheControl,
      setDefaultCache
    } = _ref2;
    const headers = await this.getHeaders();
    return _bluebird.default.try(() => _axios.default.post(`${this.filesApiUrl}/api/url?${FilesApi._buildQueryParams({
      isPublic,
      expires,
      ...(this.accountId ? {
        accountId: this.accountId
      } : {})
    })}`, {
      key,
      contentType,
      maxFileSize,
      cacheControl,
      setDefaultCache
    }, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async getSystemUploadUrl(postfix, files, options) {
    // files: [{name, size, contentType, cacheControl}]
    return _bluebird.default.try(() => this._callApi({
      method: 'POST',
      path: 'uploadfile',
      payload: {
        postfix,
        files: _lodash.default.map(files, file => _lodash.default.omit(file, ['fileDescriptor'])),
        ...(!_lodash.default.isEmpty(options) && options)
      }
    }));
  }
  async uploadFile(_ref3) {
    let {
      fileModel,
      prefix,
      isPublic,
      expires,
      progress,
      maxFileSize,
      ttl
    } = _ref3;
    const {
      type,
      name
    } = fileModel;
    const contentType = type || 'binary/octet-stream';
    const cacheControl = ttl ? `max-age=${ttl}` : 'no-cache';
    const {
      url,
      fields
    } = await this.getUploadUrl({
      key: prefix + name,
      contentType,
      maxFileSize,
      cacheControl
    }, isPublic, expires);
    const FormDataLib = _browserOrNode.isNode ? require('form-data') : FormData; // eslint-disable-line no-undef
    const formData = new FormDataLib();
    _lodash.default.forEach(fields, (value, key) => {
      formData.append(key, value);
    });
    formData.append('cache-control', cacheControl);
    formData.append('content-type', contentType);
    formData.append('File', fileModel.file, name);
    if (_browserOrNode.isNode) {
      return new _bluebird.default((resolve, reject) => {
        formData.submit(url, error => {
          if (error) return reject(error);
          resolve();
        });
      });
    }
    return _axios.default.post(url, formData, {
      onUploadProgress: _lodash.default.isFunction(progress) ? event => progress(event, name) : null
    });
  }
  async uploadSystemFiles(postfix, files, options) {
    // files: [{name, size, contentType, ttl, fileDescriptor}]
    const patchedFiles = _lodash.default.map(files, file => {
      const cacheControl = file.ttl ? `max-age=${file.ttl}` : 'no-cache';
      return {
        ...file,
        cacheControl
      };
    });
    const uploadParams = await this.getSystemUploadUrl(postfix, patchedFiles, options);
    const FormDataLib = _browserOrNode.isNode ? require('form-data') : FormData; // eslint-disable-line no-undef
    const formData = new FormDataLib();
    return _bluebird.default.map(uploadParams, async (params, index) => {
      const {
        url,
        fields
      } = params;
      const file = patchedFiles[index];
      _lodash.default.forEach(fields, (value, key) => {
        formData.append(key, value);
      });
      formData.append('cache-control', file.cacheControl);
      formData.append('content-type', file.contentType);
      formData.append('File', file.fileDescriptor, file.name);
      return _axios.default.post(url, formData);
    });
  }
  async ls(_ref4) {
    let {
      isPublic,
      prefix
    } = _ref4;
    const headers = await this.getHeaders();
    let nextToken = null;
    let totalItems = [];
    do {
      const queryParams = {
        isPublic,
        prefix,
        ...(this.accountId ? {
          accountId: this.accountId
        } : {})
      };
      if (nextToken) {
        queryParams.nextToken = nextToken;
      }
      const {
        items,
        ...rest
      } = (await _axios.default.get(`${this.filesApiUrl}/api/files?${FilesApi._buildQueryParams(queryParams)}`, headers)).data;
      nextToken = rest.nextToken;
      totalItems = _lodash.default.chain(totalItems).concat(items).sortBy().value();
    } while (nextToken);
    return totalItems;
  }
  async listFiles(_ref5) {
    let {
      isPublic,
      term,
      excludeFolderNames
    } = _ref5;
    const headers = await this.getHeaders();
    let nextToken = null;
    let items = [];
    do {
      const queryParams = {
        isPublic,
        term,
        excludeFolderNames,
        ...(this.accountId ? {
          accountId: this.accountId
        } : {})
      };
      if (nextToken) {
        queryParams.nextToken = nextToken;
      }
      const {
        files,
        ...rest
      } = (await _axios.default.get(`${this.filesApiUrl}/api/files/search?${FilesApi._buildQueryParams(queryParams)}`, headers)).data;
      nextToken = rest.nextToken;
      items = _lodash.default.chain(items).concat(files).sortBy().value();
    } while (nextToken);
    return items;
  }
  async createFolder(folderPath) {
    const headers = await this.getHeaders();
    const queryParams = this.accountId ? {
      accountId: this.accountId
    } : {};
    return _bluebird.default.try(() => _axios.default.post(`${this.filesApiUrl}/api/files?${FilesApi._buildQueryParams(queryParams)}`, {
      key: folderPath
    }, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async renameFile(key, newKey, isPublic) {
    const headers = await this.getHeaders();
    return _bluebird.default.try(() => _axios.default.put(`${this.filesApiUrl}/api/files?${FilesApi._buildQueryParams({
      key,
      isPublic,
      ...(this.accountId ? {
        accountId: this.accountId
      } : {})
    })}`, {
      newKey
    }, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async changePrivacy(key, newPrivacy, isPublic) {
    const headers = await this.getHeaders();
    return _bluebird.default.try(() => _axios.default.patch(`${this.filesApiUrl}/api/files?${FilesApi._buildQueryParams({
      key,
      isPublic,
      ...(this.accountId ? {
        accountId: this.accountId
      } : {})
    })}`, {
      newPrivacy
    }, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async delete(key, isPublic) {
    const headers = await this.getHeaders();
    return _bluebird.default.try(() => _axios.default.delete(`${this.filesApiUrl}/api/files?${FilesApi._buildQueryParams({
      key,
      isPublic,
      ...(this.accountId ? {
        accountId: this.accountId
      } : {})
    })}`, headers).then(response => _bluebird.default.resolve(response.data)));
  }
  async deleteFolder(key) {
    const headers = await this.getHeaders();
    return this._callApi({
      method: 'delete',
      path: `${this.filesApiUrl}/api/folder`,
      payload: {
        key
      },
      headers
    });
  }
}
var _default = FilesApi;
exports.default = _default;